
import { User } from "@/context/auth/types";
import { apiConfig } from "@/lib/env";
import { checkBackendHealth, isNetworkError } from "./healthCheck";

// FastAPI OAuth2 response format
interface FastAPILoginResponse {
  access_token: string;
  token_type: string;
}

// The structure expected by AuthContext
interface AuthContextLoginResponse {
  user: User;
  token: string;
}

// Enhanced login function with health check and better error handling
export const login = async (email: string, password: string): Promise<AuthContextLoginResponse> => {
  console.log(`🔑 Attempting to login with email: ${email}`);
  
  // Perform health check before login
  try {
    const healthResult = await checkBackendHealth();
    if (!healthResult.isHealthy) {
      throw new Error(`Backend is not healthy: ${healthResult.error}`);
    }
    console.log('✅ Backend health check passed, proceeding with login');
  } catch (healthError) {
    console.warn('⚠️ Health check failed, proceeding with login anyway:', healthError);
    // Continue with login even if health check fails
  }

  // Create URLSearchParams for form data
  const formData = new URLSearchParams();
  formData.append('username', email);
  formData.append('password', password);

  const loginUrl = `${apiConfig.baseUrl}/auth/login`;
  let authData: FastAPILoginResponse;

  console.log('🔧 Login URL:', loginUrl);
  console.log('🔧 API Config baseUrl:', apiConfig.baseUrl);

  // Step 1: Authenticate and get the token
  try {
    console.log('📡 Making login request to:', loginUrl);
    
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
      },
      body: formData,
      mode: 'cors' as RequestMode,
      redirect: 'follow' as RequestRedirect,
    };
    
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Login request timeout after 15 seconds')), 15000)
    );
    
    const response = await Promise.race([
      fetch(loginUrl, requestOptions),
      timeoutPromise
    ]) as Response;

    console.log('📊 Login response status:', response.status);
    console.log('📊 Login response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Login failed with status:', response.status, 'Response:', errorText);
      
      // Provide more specific error messages
      if (response.status === 401) {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      } else if (response.status >= 500) {
        throw new Error('Server error occurred. Please try again later.');
      } else {
        throw new Error(`Login failed: ${response.status} ${response.statusText}`);
      }
    }

    // Parse JSON response
    let responseData;
    try {
      const responseText = await response.text();
      console.log('🔧 Raw login response text:', responseText);
      responseData = JSON.parse(responseText);
      console.log('✅ Login response data:', responseData);
    } catch (parseError) {
      console.error('❌ Failed to parse login response as JSON:', parseError);
      throw new Error('Invalid response format from server');
    }

    if (!responseData.access_token) {
      console.error('❌ Login response missing access_token:', responseData);
      throw new Error('Login response missing access token.');
    }
    
    authData = responseData as FastAPILoginResponse;
    console.log('✅ Login step 1 successful, received token:', authData.access_token.substring(0, 10) + '...');

  } catch (error) {
    console.error('🔧 Detailed login fetch error:', error);
    
    if (isNetworkError(error)) {
      throw new Error(`Network error: Cannot connect to authentication server. Please check your internet connection and try again.`);
    }
    
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(errorMessage);
  }

  // Step 2: Use the token to verify and get user details
  try {
    const userDetails: User = await verifyToken(authData.access_token);
    console.log('✅ Login step 2 successful, received user details:', userDetails);

    return {
      user: userDetails,
      token: authData.access_token
    };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("❌ Error processing token verification during login:", errorMessage);
    throw new Error(`Failed to verify authentication token: ${errorMessage}`);
  }
};

export const verifyToken = async (token: string, retryCount: number = 0): Promise<User> => {
  const verifyUrl = `${apiConfig.baseUrl}/auth/verify-token`;
  
  try {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ token }),
      mode: 'cors' as RequestMode,
      redirect: 'follow' as RequestRedirect,
    };
    
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout after 10 seconds')), 10000)
    );
    
    const response = await Promise.race([
      fetch(verifyUrl, requestOptions),
      timeoutPromise
    ]) as Response;
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token verification failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // Parse JSON response
    const responseText = await response.text();
    const responseData = JSON.parse(responseText);
    
    // Validate the structure of the response data
    if (!responseData || typeof responseData.id === 'undefined') {
      throw new Error('Invalid user data received from token verification.');
    }

    // Transform the API response to match our User type
    const transformedUser: User = {
      id: responseData.id,
      email: responseData.email,
      username: responseData.username,
      firstName: responseData.first_name,
      lastName: responseData.last_name,
      role: responseData.is_superuser ? "admin" : "user",
      permissions: [],
      isActive: responseData.is_active,
      isSuperuser: responseData.is_superuser
    };
    
    return transformedUser;
    
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(errorMessage);
  }
};
